// src/utils/mockDataWHSRecords.ts
// ─────────────────────────────────────────────────────────────────
// Platform WHS Records Store — Phase 4
//
// When a crew member submits a WHS report via the mobile app,
// whsInboxService.createPlatformWHSRecord() writes a corresponding
// record here. This is the bi-directional sync (4C-02):
//   Mobile submission → WHS Inbox (MobileComms) → Platform WHS record
//
// At beta: replaced by POST /api/whs/incidents | /api/whs/inspections
// ─────────────────────────────────────────────────────────────────

export type WHSRecordModule = 'incidents' | 'inspections' | 'swms-flags'
export type WHSRecordStatus = 'OPEN' | 'UNDER_INVESTIGATION' | 'CORRECTIVE_ACTION' | 'CLOSED'

export interface WHSRecord {
  id:          string
  module:      WHSRecordModule
  reference:   string            // e.g. 'INC-2026-0042', 'INSP-CA-0018'
  sourceType:  string            // 'MOBILE_SUBMISSION' | 'MANUAL_PM' | 'AUTO_PLATFORM'
  submissionId:string | null     // links back to whsInboxService submission
  jobId:       string
  jobNumber:   string
  title:       string
  description: string
  severity:    'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
  status:      WHSRecordStatus
  createdAt:   string
  createdById: string
  createdByName:string
  assignedToId:string | null
  closedAt:    string | null
}

// ── Module-level store ─────────────────────────────────────────────
let _records: WHSRecord[] = []
let _seq = 100

function nextRef(module: WHSRecordModule): string {
  const prefix = module === 'incidents' ? 'INC' : module === 'inspections' ? 'INSP-CA' : 'SWMS-FLAG'
  return `${prefix}-2026-${String(++_seq).padStart(4,'0')}`
}

// ── Seed data — existing WHS records ──────────────────────────────
// Corresponds to the mock WHS submissions in mockDataMobile.ts so
// that "View in WHS Module" links have real records to show.
const SEED_RECORDS: Omit<WHSRecord,'id'>[] = [
  {
    module:       'incidents',
    reference:    'INC-2026-0038',
    sourceType:   'MOBILE_SUBMISSION',
    submissionId: 'whs001',
    jobId:        'j1',
    jobNumber:    'TP-2026-001',
    title:        'Near-miss — vehicle entered exclusion zone',
    description:  'Delivery vehicle entered exclusion zone while crew were working in Pit 1A. No contact made. Driver reversed after receiving hand signal from TC.',
    severity:     'HIGH',
    status:       'UNDER_INVESTIGATION',
    createdAt:    '2026-07-09T11:32:00Z',
    createdById:  'u4',
    createdByName:'Sarah Lee (PM)',
    assignedToId: 'u4',
    closedAt:     null,
  },
  {
    module:       'inspections',
    reference:    'INSP-CA-0091',
    sourceType:   'MOBILE_SUBMISSION',
    submissionId: 'whs002',
    jobId:        'j1',
    jobNumber:    'TP-2026-001',
    title:        'Corrective action — damaged pedestrian barrier',
    description:  'TMP barrier on north kerb partially dislodged. Crew self-corrected on site. Inspect and re-stake before next shift.',
    severity:     'MEDIUM',
    status:       'CORRECTIVE_ACTION',
    createdAt:    '2026-07-08T14:10:00Z',
    createdById:  'u4',
    createdByName:'Sarah Lee (PM)',
    assignedToId: null,
    closedAt:     null,
  },
  {
    module:       'incidents',
    reference:    'INC-2026-0039',
    sourceType:   'MOBILE_SUBMISSION',
    submissionId: 'whs003',
    jobId:        'j2',
    jobNumber:    'TP-2026-002',
    title:        'Near-miss — delivery truck entered Pit 2B exclusion zone',
    description:  'HIGH severity near-miss at Pacific Mwy On-ramp. Delivery truck entered Pit 2B exclusion zone while crew present. Under investigation.',
    severity:     'HIGH',
    status:       'UNDER_INVESTIGATION',
    createdAt:    '2026-07-11T08:15:00Z',
    createdById:  'u5',
    createdByName:'Mike Walsh (PM)',
    assignedToId: 'u5',
    closedAt:     null,
  },
]

// Initialise store with seeds
_records = SEED_RECORDS.map((r,i) => ({ ...r, id: `whsr_${String(i+1).padStart(3,'0')}` }))

// ── Write ──────────────────────────────────────────────────────────
export function addWHSRecord(
  record: Omit<WHSRecord, 'id' | 'reference' | 'createdAt'>
): WHSRecord {
  const full: WHSRecord = {
    ...record,
    id:        `whsr_${String(++_seq).padStart(3,'0')}`,
    reference: nextRef(record.module),
    createdAt: new Date().toISOString(),
  }
  _records.push(full)
  return full
}

// ── Read ───────────────────────────────────────────────────────────
export interface GetWHSRecordsFilter {
  module?:       WHSRecordModule
  jobId?:        string
  submissionId?: string
  status?:       WHSRecordStatus
}

export function getWHSRecords(filter?: GetWHSRecordsFilter): WHSRecord[] {
  let results = [..._records]
  if (filter?.module)       results = results.filter(r => r.module === filter.module)
  if (filter?.jobId)        results = results.filter(r => r.jobId === filter.jobId)
  if (filter?.submissionId) results = results.filter(r => r.submissionId === filter.submissionId)
  if (filter?.status)       results = results.filter(r => r.status === filter.status)
  return results.slice().sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
}

export function getWHSRecordById(id: string): WHSRecord | null {
  return _records.find(r => r.id === id) ?? null
}

export function getWHSRecordBySubmissionId(submissionId: string): WHSRecord | null {
  return _records.find(r => r.submissionId === submissionId) ?? null
}

export function updateWHSRecordStatus(id: string, status: WHSRecordStatus): WHSRecord | null {
  const idx = _records.findIndex(r => r.id === id)
  if (idx < 0) return null
  _records[idx] = {
    ..._records[idx],
    status,
    closedAt: status === 'CLOSED' ? new Date().toISOString() : _records[idx].closedAt,
  }
  return _records[idx]
}

export function _resetWHSRecords(): void {
  _records = SEED_RECORDS.map((r,i) => ({ ...r, id: `whsr_${String(i+1).padStart(3,'0')}` }))
  _seq = 100
}
